import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  ColorOption,
  ContentOpener,
  ContentPopper,
  FONT_SIZES,
  FONT_WEIGHTS,
  ProductColorOptionsProps,
  tabletAndUp,
} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';
import {useColorSelectionOpenedState} from '../utils';
import {ProductSelectionViewProps} from './ProductSelectionView';

export const ProductSelectionList: FC<
  ProductSelectionViewProps & {
    colorOptions: ProductColorOptionsProps['options'];
  }
> = ({className, colorOptions, ColorSelectionLayoutComponent}) => {
  const {hide, isOpen, open} = useColorSelectionOpenedState();
  const {t} = useTranslation();

  const colorSelectionEnabled = colorOptions.length > 1;
  const selectedOption = colorOptions.find(option => option.isActive);

  return (
    <Container className={className} data-testid="productColorOptions">
      <Label data-testid="productColorOptionsListTitle">{t('Colour')}</Label>
      <StyledContentPopper
        LayoutComponent={ColorSelectionLayoutComponent}
        data-testid="productColorOptionsList"
        onClose={hide}
        open={isOpen}
        onOpenerClick={() => {
          if (colorSelectionEnabled) {
            if (isOpen) {
              hide();
            } else {
              open({replaceHistory: true});
            }
          }
        }}
        openerContent={
          <ColorOption
            dataTestId={selectedOption?.['data-testid']}
            label={selectedOption?.name ?? ''}
            selected={false}
            thumbnailUrl={selectedOption?.image?.url}
          />
        }
        bodyContent={
          colorSelectionEnabled && (
            <ColorOptionContainer data-testid="productSelectionListColorOptions">
              {colorOptions.map(option => {
                return (
                  <StyledColorOption
                    dataTestId={option['data-testid']}
                    key={`color-option-${option.name}`}
                    label={option.name}
                    large
                    onClick={option.onClick}
                    path={option.path}
                    selected={option.isActive}
                    thumbnailUrl={option.image.url}
                  />
                );
              })}
            </ColorOptionContainer>
          )
        }
      />
    </Container>
  );
};

export const FlyoutContentHeader = () => {
  const {t} = useTranslation();

  return (
    <Header>
      <Headline data-testid="colorFlyoutHeadline">{t('Select color')}</Headline>
    </Header>
  );
};

const Container = styled.div``;
const StyledContentOpener = styled(ContentOpener)``;

const StyledContentPopper = styled(ContentPopper)`
  ${StyledContentOpener} {
    display: flex;
    align-items: center;
  }
`;

const ColorOptionContainer = styled.div`
  overflow: auto;
  ${tabletAndUp(css`
    max-height: 240px;
  `)}
`;

const Label = styled.span`
  display: block;
  font-size: ${FONT_SIZES.px16};
  color: ${COLORS.Black};
  font-weight: ${FONT_WEIGHTS.bold};
  margin-bottom: 1rem;
`;

const StyledColorOption = styled(ColorOption)`
  background: none;
  transition: background-color 200ms ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${COLORS.Grey4};
  }
`;

const Header = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  padding: 0 1rem;
  overscroll-behavior: contain;
  width: 100%;
`;

const Headline = styled.h3`
  font-size: ${FONT_SIZES.px18};
  font-weight: ${FONT_WEIGHTS.bold};
  margin: 0.25rem 0;
`;
